import { useState } from 'react';
import { signInWithGoogle } from '@heyroot/auth-shared';
import { Button } from '@heyroot/backoffice-ui';
import { useAdminAuth } from '../lib/auth-context';

export function LoginPage() {
  const auth = useAdminAuth();
  const [signInError, setSignInError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);

  const signIn = async () => {
    setSignInError(null);
    setSigningIn(true);
    try {
      // Popup flow (same proven path as apps/backoffice via the shared helper).
      // Restrict to the heyroot.ai Google Workspace and always show the account
      // chooser. onIdTokenChanged in AuthProvider drives the rest on success.
      await signInWithGoogle({ hd: 'heyroot.ai', prompt: 'select_account' });
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      // Closing the account chooser (or a superseded popup request) is normal
      // user behavior, not a failure — reset to idle without a scary banner.
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return;
      }
      const msg = (err as { message?: string }).message ?? 'Sign-in failed.';
      console.error('[admin-login] signInWithGoogle error:', err);
      setSignInError(msg);
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-6 rounded-control border border-border bg-surface-elevated p-10">
        <h1 className="text-heading-2 text-primary">HeyRoot Admin</h1>

        {auth.status === 'not_provisioned' && (
          <p className="max-w-xs text-center text-sm text-warning-text">
            Your Google account is not provisioned as an admin. Contact the platform team.
          </p>
        )}

        {signInError && (
          <p className="max-w-xs break-words text-center text-sm text-danger-text">{signInError}</p>
        )}

        <Button onClick={signIn} disabled={signingIn}>
          {signingIn ? 'Signing in…' : 'Sign in with Google'}
        </Button>
      </div>
    </div>
  );
}
